import { PDF_TOOLS, type PdfToolId } from '@/data/pdfTools';
import { usePdfStore } from '@/stores/pdfStore';
import PdfExportMenu from './PdfExportMenu';
import PdfSaveButton from './PdfSaveButton';

const ICONS: Record<PdfToolId, string> = {
  pointer: '↖',
  text: 'T',
  highlight: '▮',
  brush: '✎',
  eraser: '⌫',
  image: '▣',
  shapes: '◯',
  sign: '✍',
  hand: '✋',
};

interface PdfToolbarProps {
  onDownload: () => void;
  onSendEmail: () => void;
  onSave: () => void;
  onSendToCanvas: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onProtect: () => void;
  onFinalize: () => void;
  busy?: boolean;
  dirty?: boolean;
}

export default function PdfToolbar({
  onDownload,
  onSendEmail,
  onSave,
  onSendToCanvas,
  onUndo,
  onRedo,
  onProtect,
  onFinalize,
  busy,
  dirty,
}: PdfToolbarProps) {
  const tool = usePdfStore((s) => s.tool);
  const setTool = usePdfStore((s) => s.setTool);
  const activePageIndex = usePdfStore((s) => s.activePageIndex);
  const canUndo = usePdfStore((s) => s.canUndo(s.activePageIndex));
  const canRedo = usePdfStore((s) => s.canRedo(s.activePageIndex));
  const protectionEnabled = usePdfStore((s) => !!s.protection?.enabled);

  return (
    <div className="pdfe-toolbar">
      <div className="pdfe-toolbar-left">
        <button
          type="button"
          className="pdfe-icon-btn"
          disabled={!canUndo || busy}
          onClick={onUndo}
          title="Undo"
          aria-label="Undo"
        >
          ↶
        </button>
        <button
          type="button"
          className="pdfe-icon-btn"
          disabled={!canRedo || busy}
          onClick={onRedo}
          title="Redo"
          aria-label="Redo"
        >
          ↷
        </button>
      </div>

      <div className="pdfe-toolbar-tools" role="toolbar" aria-label="PDF tools">
        {PDF_TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pdfe-tool${tool === t.id ? ' is-active' : ''}`}
            onClick={() => setTool(t.id)}
            disabled={busy}
            title={t.label}
          >
            <span className="pdfe-tool-icon" aria-hidden>
              {ICONS[t.id]}
            </span>
            <span className="pdfe-tool-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="pdfe-toolbar-right">
        <button
          type="button"
          className="btn btn-secondary pdfe-finalize-btn"
          disabled={busy}
          onClick={onFinalize}
          title="Bake all page annotations into the PDF (make final)"
        >
          Finalize
        </button>
        <button
          type="button"
          className={`btn btn-secondary pdfe-protect-btn${protectionEnabled ? ' is-protected' : ''}`}
          disabled={busy}
          onClick={onProtect}
          title={
            protectionEnabled
              ? 'Password protection enabled — click to change'
              : 'Password protect download'
          }
        >
          {protectionEnabled ? 'Protected' : 'Protect'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy}
          onClick={onSendToCanvas}
          title="Import pages into a Craftara design"
        >
          Send to canvas
        </button>
        <PdfSaveButton
          busy={busy}
          dirty={dirty}
          tone="light"
          onSave={onSave}
        />
        <PdfExportMenu
          busy={busy}
          tone="light"
          onDownload={onDownload}
          onSendEmail={onSendEmail}
        />
        <span className="pdfe-toolbar-page-hint" aria-hidden>
          {activePageIndex + 1}
        </span>
      </div>
    </div>
  );
}
